var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isValid, } from '../../../../kaylee/EquipmentStates';
import { Sutl } from '../../../../sutl';
import { assertIsSetParam, } from './EquipmentServiceDriver';
import { handleTransportError, } from './ParamBridge';
import { StateCollection } from './State';
import { KeyedValueBridge, ValueBridge } from './ValueBridge';
export class CamerasBridge {
    constructor(state, keys, control, config, controlInfo, powerState, framework, log) {
        this.state = state;
        this.keys = keys;
        this.control = control;
        this.config = config;
        this.controlInfo = controlInfo;
        this.powerState = powerState;
        this.framework = framework;
        this.log = log;
        this.queryHandlers = {};
        this.setFilters = {};
        this.setHandlers = {};
        this.getTag = 'cameras';
        this.stateSubscription = this.state.subscribe((prevValue) => {
            const newKeys = Object.assign({}, this.keys.value);
            // Remove old keys.
            for (const key of Object.keys(newKeys)) {
                if (!(key in this.state.value))
                    delete newKeys[key];
            }
            // Add new keys.
            for (const key of Object.keys(this.state.value)) {
                if (!(key in newKeys)) {
                    newKeys[key] = Sutl.generateFirebaseId();
                }
            }
            this.keys.update(newKeys);
            if (this.framework.phase === 'controlling')
                this.framework.recomputeCaps();
        });
    }
    destroy() {
        this.state.unsubscribe(this.stateSubscription);
    }
    getInitialValues() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.controlInfo.canGet(this.powerState.value))
                return;
            let cameras = {};
            const keys = {};
            if (!('type' in this.control)) {
                if ('getKey' in this.control) {
                    for (const [configKey, cameraConfig] of Object.entries((_a = this.config) !== null && _a !== void 0 ? _a : {})) {
                        const driverKey = this.control.getKey(cameraConfig);
                        try {
                            cameras[driverKey] = yield this.control.get(driverKey, [this.getTag]);
                        }
                        catch (error) {
                            handleTransportError(this.log, error);
                        }
                        keys[driverKey] = configKey;
                    }
                }
                else {
                    try {
                        cameras = yield this.control.get([this.getTag]);
                    }
                    catch (error) {
                        handleTransportError(this.log, error);
                    }
                    for (const driverKey of Object.keys(cameras)) {
                        keys[driverKey] = Sutl.generateFirebaseId();
                    }
                }
            }
            else {
                try {
                    cameras = yield this.control.get(this.config, [this.getTag]);
                }
                catch (error) {
                    handleTransportError(this.log, error);
                }
                const keyMap = {};
                if (this.control.getKey !== undefined) {
                    for (const [configKey, config] of Object.entries(this.config)) {
                        try {
                            keyMap[this.control.getKey(config)] = configKey;
                        }
                        catch (error) {
                            this.log.error(error.message);
                        }
                    }
                }
                for (const driverKey of Object.keys(cameras)) {
                    keys[driverKey] = (_b = keyMap[driverKey]) !== null && _b !== void 0 ? _b : Sutl.generateFirebaseId();
                }
            }
            this.keys.update(keys);
            this.state.update(cameras);
        });
    }
    poll() {
        return __awaiter(this, void 0, void 0, function* () {
            this.log.warning('poll not implemented for cameras state bridge');
        });
    }
    validate() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!('validate' in this.control))
                return undefined;
            const cameras = {};
            for (const [configKey, cameraConfig] of Object.entries((_a = this.config) !== null && _a !== void 0 ? _a : {})) {
                const driverKey = this.control.getKey(cameraConfig);
                try {
                    cameras[configKey] = this.control.validate
                        ? yield this.control.validate(cameraConfig, driverKey)
                        : 'missing';
                }
                catch (error) {
                    handleTransportError(this.log, error);
                }
            }
            return { cameras };
        });
    }
}
export class CameraMoveBridge extends KeyedValueBridge {
    constructor(keys, control, controlInfo, powerState, transport, valueCallback, log) {
        super('cameraMove', new StateCollection(), // dummy state, not used since write-only
        control, () => Object.keys(this.keys.value), controlInfo, powerState, transport, valueCallback, log);
        this.keys = keys;
        // No set filter. We need to process all camera move operations.
        this.removeSetCommands = false;
    }
    convertFromParam(value) {
        var _a, _b;
        const key = Object.keys(this.keys.value).find(key => this.keys.value[key] === value.cameraId);
        if (key === undefined) {
            throw new Error(`key map does not contain entry for id = ${value.cameraId}`);
        }
        const convertedValue = value.action === 'stop'
            ? { action: 'stop' }
            : 'direction' in value
                ? { action: 'start', direction: (_a = value.direction) !== null && _a !== void 0 ? _a : 'up' }
                : 'holdRepeatMs' in value
                    ? { action: 'repeat', holdRepeatMs: (_b = value.holdRepeatMs) !== null && _b !== void 0 ? _b : 0 }
                    : undefined;
        if (convertedValue === undefined) {
            throw new Error(`invalid camera move value: ${JSON.stringify(value)}`);
        }
        return { key, value: convertedValue };
    }
    // not used since write-only
    convertToParam(key, value) {
        const cameraId = this.keys.value[key];
        if (cameraId === undefined) {
            throw new Error(`key map does not contain entry for key = ${key}`);
        }
        return Object.assign({ cameraId }, value);
    }
}
export class CameraPositionBridge {
    constructor(keys, control, config, controlInfo, powerState, transport, valueCallback, log) {
        this.keys = keys;
        this.control = control;
        this.config = config;
        this.controlInfo = controlInfo;
        this.powerState = powerState;
        this.valueCallback = valueCallback;
        this.log = log;
        this.queryHandlers = {};
        this.setFilters = {};
        this.setHandlers = {};
        this.setHandlers.cameraPosition = this.onSet.bind(this);
    }
    destroy() { }
    getInitialValues() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    onSet(param, setValue) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!('get' in this.control))
                throw new Error('expected readable control');
            if (!this.controlInfo.canGet(this.powerState.value))
                return;
            const request = setValue;
            const configKey = Object.keys(this.config).find(key => {
                const config = this.config[key];
                return 'id' in config && config.id === request.cameraId;
            });
            if (configKey === undefined) {
                this.log.error(`camera id ${request.cameraId} not found in config`);
                this.valueCallback('cameraPosition', {
                    cameraId: request.cameraId,
                    requestId: request.requestId,
                    error: 'noCamera',
                });
                return;
            }
            const driverKey = Object.keys(this.keys.value).find(key => this.keys.value[key] === configKey);
            if (driverKey === undefined) {
                this.log.error(`key map does not contain entry for id = ${request.cameraId}`);
                this.valueCallback('cameraPosition', {
                    cameraId: request.cameraId,
                    requestId: request.requestId,
                    error: 'noCamera',
                });
                return;
            }
            const getTag = `get.cameraPosition.${driverKey}`;
            if (this.control.get === undefined)
                throw new Error(`expected readable control`);
            try {
                const response = yield this.control.get(driverKey, [getTag]);
                this.valueCallback('cameraPosition', Object.assign({ cameraId: request.cameraId, requestId: request.requestId, pan: response.pan, tilt: response.tilt }, ('zoom' in response ? { zoom: response.zoom } : undefined)));
            }
            catch (error) {
                this.log.error(error);
                this.valueCallback('cameraPosition', {
                    cameraId: request.cameraId,
                    requestId: request.requestId,
                    error: 'noCamera',
                });
            }
        });
    }
    poll() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
}
export class CameraPresetBridge extends ValueBridge {
    constructor(config, state, control, controlInfo, powerState, transport, valueCallback, log) {
        super('cameraPreset', state, control, controlInfo, powerState, transport, valueCallback, log);
        this.config = config;
    }
    convertFromParam(presetId) {
        var _a, _b, _c, _d;
        if (presetId === null)
            return undefined;
        // Zoom
        const presetConfig = (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.cameraPresets) === null || _b === void 0 ? void 0 : _b[presetId];
        if (presetConfig !== undefined)
            return presetConfig.presetIndex;
        // Visca
        for (const cameraConfig of Object.values((_c = this.config.cameras) !== null && _c !== void 0 ? _c : {})) {
            if (!('presets' in cameraConfig))
                continue;
            for (const presetConfigId of Object.keys((_d = cameraConfig.presets) !== null && _d !== void 0 ? _d : {})) {
                if (presetConfigId === presetId)
                    return presetId;
            }
        }
        this.log.error(`no camera preset config for presetId: ${presetId}`);
        return this.state.value;
    }
    convertToParam(presetIndex) {
        var _a, _b, _c, _d;
        if (presetIndex === undefined)
            return null;
        // Zoom
        const presets = (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.cameraPresets) !== null && _b !== void 0 ? _b : {};
        for (const presetId of Object.keys(presets)) {
            if (presets[presetId].presetIndex === presetIndex)
                return presetId;
        }
        // Visca
        for (const cameraConfig of Object.values((_c = this.config.cameras) !== null && _c !== void 0 ? _c : {})) {
            if (!('presets' in cameraConfig))
                continue;
            for (const presetConfigId of Object.keys((_d = cameraConfig.presets) !== null && _d !== void 0 ? _d : {})) {
                if (presetConfigId === presetIndex)
                    return presetIndex;
            }
        }
        this.log.warning(`no preset configured for preset index: ${presetIndex}`);
        return null;
    }
}
export class CameraPresetConfigBridge {
    constructor(state, control, config, controlInfo, powerState, transport, valueCallback, log) {
        this.state = state;
        this.control = control;
        this.config = config;
        this.controlInfo = controlInfo;
        this.powerState = powerState;
        this.transport = transport;
        this.valueCallback = valueCallback;
        this.log = log;
        this.eKeyIsValid = {};
        this.isValid = true;
        this.keyMap = {}; // keyMap[eKey] = dKey
        this.name = 'cameraPreset';
        this.pollName = 'cameraPreset';
        this.queryHandlers = {};
        this.setFilters = {};
        this.setHandlers = {};
        this.stateSubscription = this.state.subscribe(() => {
            const presetId = this.state.value;
            let ePresetId;
            for (const [eKey, dKey] of Object.entries(this.keyMap)) {
                if (dKey === presetId) {
                    ePresetId = eKey;
                    break;
                }
            }
            this.valueCallback(this.name, ePresetId !== null && ePresetId !== void 0 ? ePresetId : null);
        });
        this.queryHandlers[this.name] = this.onQuery.bind(this);
        this.setHandlers[this.name] = this.onSet.bind(this);
    }
    buildKeyMap() {
        this.keyMap = {};
        for (const [eKey, config] of Object.entries(this.config)) {
            const dKey = this.control.getKey(config);
            this.keyMap[eKey] = dKey;
        }
    }
    destroy() {
        if (this.stateSubscription !== undefined) {
            this.state.unsubscribe(this.stateSubscription);
        }
    }
    getInitialValues() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getValues(false);
        });
    }
    getValues(isPoll) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.controlInfo.canGet(this.powerState.value))
                return;
            for (const [eKey, dKey] of Object.entries(this.keyMap)) {
                if (!this.eKeyIsValid[eKey])
                    continue;
                const getTag = `get.${this.name}.${dKey}`;
                if (isPoll && this.transport.hasCommands(getTag))
                    continue;
                const tags = isPoll ? ['poll', getTag] : [getTag];
                try {
                    const isActive = yield this.control.get(dKey, tags);
                    if (isActive) {
                        this.state.update(dKey);
                        return;
                    }
                }
                catch (error) {
                    handleTransportError(this.log, error);
                }
            }
            this.state.update(undefined);
        });
    }
    onQuery(param) {
        var _a;
        this.valueCallback(param, (_a = this.state.value) !== null && _a !== void 0 ? _a : null);
    }
    onSet(param, setValue) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.controlInfo.canSet(this.powerState.value)) {
                this.valueCallback(this.name, (_a = this.state.value) !== null && _a !== void 0 ? _a : null);
                return;
            }
            if (setValue === null) {
                this.state.update(undefined);
                return;
            }
            const setTag = `set.${this.name}`;
            this.transport.removeCommands(['poll', setTag]);
            const eKey = setValue;
            const dKey = (_b = this.keyMap[eKey]) !== null && _b !== void 0 ? _b : '';
            try {
                const value = yield this.control.set(dKey, true, [setTag]);
                if (value)
                    this.state.update(dKey);
            }
            catch (error) {
                handleTransportError(this.log, error);
            }
        });
    }
    poll() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getValues(true);
        });
    }
    validate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.buildKeyMap();
            if (!this.control.validate) {
                this.eKeyIsValid = Object.fromEntries(Object.keys(this.keyMap).map(key => [key, true]));
                return undefined;
            }
            const results = {};
            this.eKeyIsValid = {};
            this.isValid = true;
            for (const [eKey, config] of Object.entries(this.config)) {
                const result = yield this.control.validate(config, undefined);
                this.eKeyIsValid[eKey] = isValid(result);
                if (!this.eKeyIsValid[eKey])
                    this.isValid = false;
                results[eKey] = result;
            }
            return { cameraPresets: results };
        });
    }
}
export class CameraSelectedBridge extends ValueBridge {
    constructor(state, keys, control, controlInfo, powerState, transport, valueCallback, log) {
        super('cameraSelected', state, control, controlInfo, powerState, transport, valueCallback, log);
        this.keys = keys;
    }
    convertFromParam(value) {
        const key = Object.keys(this.keys.value).find(key => this.keys.value[key] === value);
        if (key === undefined) {
            throw new Error(`key map does not contain entry for id = ${value}`);
        }
        return key;
    }
    convertToParam(value) {
        if (value === undefined)
            return null;
        const id = this.keys.value[value];
        if (id === undefined) {
            throw new Error(`key map does not contain entry for key = ${value}`);
        }
        return id;
    }
}
export class CameraTrackingBridge extends KeyedValueBridge {
    constructor(state, keys, control, controlInfo, powerState, transport, valueCallback, log) {
        super('cameraTracking', state, control, () => Object.keys(this.keys.value), controlInfo, powerState, transport, valueCallback, log);
        this.keys = keys;
        assertIsSetParam(this.name);
        this.setFilters[this.name] = this.setFilter.bind(this);
    }
    convertFromParam(value) {
        const key = Object.keys(this.keys.value).find(key => this.keys.value[key] === value.cameraId);
        if (key === undefined) {
            throw new Error(`key map does not contain entry for id = ${value.cameraId}`);
        }
        return { key, value: value.value };
    }
    convertToParam(key, value) {
        const cameraId = this.keys.value[key];
        if (cameraId === undefined) {
            throw new Error(`key map does not contain entry for key = ${key}`);
        }
        return { cameraId, value };
    }
    setFilter(param, value, compParam, compValue) {
        if (param !== compParam)
            return true;
        const cameraId = value.cameraId;
        const compCameraId = compValue.cameraId;
        return cameraId !== compCameraId;
    }
}
